import { fetchConfig } from '@theme/utilities';
import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';

const EDITOR_SELECTOR = '[data-skovn-cart-variant-editor]';
const OPTION_SELECTOR = '[data-skovn-cart-option]';

const getEditorData = (editor) => {
  const dataElement = editor.nextElementSibling;
  if (!(dataElement instanceof HTMLScriptElement) || !dataElement.matches('[data-skovn-cart-variant-data]')) {
    return null;
  }

  try {
    return JSON.parse(dataElement.textContent || '{}');
  } catch (error) {
    console.error('[Skovn cart variants] Invalid variant data', error);
    return null;
  }
};

const getSelectedOptions = (editor) => {
  return Array.from(editor.querySelectorAll(OPTION_SELECTOR)).map((option) => {
    return option.querySelector('[data-skovn-cart-selected-value]')?.textContent?.trim() || '';
  });
};

const findVariant = (variants, options) => {
  return variants.find((variant) => {
    return Array.isArray(variant.options) && variant.options.every((value, index) => value === options[index]);
  });
};

const refreshChoiceAvailability = (editor) => {
  const data = getEditorData(editor);
  if (!data?.variants) return;

  const selectedOptions = getSelectedOptions(editor);
  const optionPickers = Array.from(editor.querySelectorAll(OPTION_SELECTOR));

  optionPickers.forEach((picker) => {
    const optionIndex = Number(picker.dataset.optionIndex);
    picker.querySelectorAll('[data-skovn-cart-option-value]').forEach((choice) => {
      if (!(choice instanceof HTMLButtonElement)) return;

      const candidateOptions = [...selectedOptions];
      candidateOptions[optionIndex] = choice.dataset.skovnCartOptionValue || '';
      const candidateVariant = findVariant(data.variants, candidateOptions);
      const isCurrent = candidateOptions[optionIndex] === selectedOptions[optionIndex];
      choice.disabled = !isCurrent && (!candidateVariant || candidateVariant.available === false);
    });
  });
};

const getSectionsToUpdate = () => {
  return Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
    .map((component) => component.dataset.sectionId)
    .filter(Boolean);
};

const setStatus = (editor, message, isError = false) => {
  const status = editor.querySelector('[data-skovn-cart-variant-status]');
  if (!(status instanceof HTMLElement)) return;
  status.textContent = message;
  status.classList.toggle('is-error', isError);
};

const switchVariant = async (editor, optionIndex, optionValue) => {
  const data = getEditorData(editor);
  if (!data?.variants) return;

  const selectedOptions = getSelectedOptions(editor);
  selectedOptions[optionIndex] = optionValue;
  const nextVariant = findVariant(data.variants, selectedOptions);
  const currentVariantId = Number(editor.dataset.currentVariantId);

  if (!nextVariant || nextVariant.available === false) {
    setStatus(editor, 'This combination is unavailable.', true);
    return;
  }

  if (Number(nextVariant.id) === currentVariantId) return;

  const lineKey = editor.dataset.lineKey;
  const quantity = Number(editor.dataset.quantity || 1);
  const sections = getSectionsToUpdate();
  const deferredPromise = CartLinesUpdateEvent.createPromise();

  editor.classList.add('is-updating');
  setStatus(editor, 'UPDATING…');

  editor.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'update',
      context: 'cart',
      lines: [
        { id: lineKey || '', quantity: 0 },
        { id: String(nextVariant.id), quantity },
      ],
      promise: deferredPromise.promise,
    })
  );

  try {
    const newLine = {
      id: nextVariant.id,
      quantity,
      properties: data.properties || {},
    };

    if (data.sellingPlan) newLine.selling_plan = data.sellingPlan;

    const addResponse = await fetch(
      Theme.routes.cart_add_url,
      fetchConfig('json', {
        body: JSON.stringify({ items: [newLine] }),
      })
    );
    const addResult = await addResponse.json();

    if (!addResponse.ok || addResult.status || addResult.errors) {
      throw new Error(addResult.description || addResult.message || addResult.errors || 'Unable to add this variant.');
    }

    const changeResponse = await fetch(
      Theme.routes.cart_change_url,
      fetchConfig('json', {
        body: JSON.stringify({
          id: lineKey,
          quantity: 0,
          sections: sections.join(','),
          sections_url: window.location.pathname,
        }),
      })
    );
    const cart = await changeResponse.json();

    if (!changeResponse.ok || cart.errors) {
      throw new Error(cart.description || cart.message || cart.errors || 'Unable to update this item.');
    }

    deferredPromise.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        sections: cart.sections,
        items: cart.items,
        itemCount: cart.item_count,
        source: 'skovn-cart-variant-editor',
        didError: false,
      },
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unable to update this item.';
    setStatus(editor, message, true);
    deferredPromise.reject(error);
    editor.dispatchEvent(
      new CartErrorEvent({
        error: message,
        code: 'INVALID',
      })
    );
  } finally {
    editor.classList.remove('is-updating');
  }
};

document.addEventListener('toggle', (event) => {
  const details = event.target;
  if (!(details instanceof HTMLDetailsElement) || !details.matches(OPTION_SELECTOR) || !details.open) return;

  const editor = details.closest(EDITOR_SELECTOR);
  if (!(editor instanceof HTMLElement)) return;

  editor.querySelectorAll(`${OPTION_SELECTOR}[open]`).forEach((otherDetails) => {
    if (otherDetails !== details) otherDetails.removeAttribute('open');
  });
  refreshChoiceAvailability(editor);
}, true);

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const choice = target.closest('[data-skovn-cart-option-value]');
  if (choice instanceof HTMLButtonElement) {
    const picker = choice.closest(OPTION_SELECTOR);
    const editor = choice.closest(EDITOR_SELECTOR);
    if (!(picker instanceof HTMLDetailsElement) || !(editor instanceof HTMLElement)) return;

    const optionIndex = Number(picker.dataset.optionIndex);
    const optionValue = choice.dataset.skovnCartOptionValue || '';
    picker.open = false;
    switchVariant(editor, optionIndex, optionValue);
    return;
  }

  document.querySelectorAll(`${OPTION_SELECTOR}[open]`).forEach((details) => {
    if (!details.contains(target)) details.removeAttribute('open');
  });
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  document.querySelectorAll(`${OPTION_SELECTOR}[open]`).forEach((details) => details.removeAttribute('open'));
});
